
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Save, Search, Star, Trash2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

interface SavedSearch {
  id: string;
  name: string;
  criteria: {
    searchTerm: string;
    selectedCounties: string[];
    selectedWaivers: string[];
    selectedCareLevel: string;
    priceRange: [number, number];
    bedsAvailable: number;
    ageGroup: string;
    genderAccepted: string;
  };
  created_at: string;
}

interface SavedSearchesProps {
  currentCriteria: {
    searchTerm: string;
    selectedCounties: string[];
    selectedWaivers: string[];
    selectedCareLevel: string;
    priceRange: [number, number];
    bedsAvailable: number;
    ageGroup: string;
    genderAccepted: string;
  };
  onLoadSearch: (criteria: any) => void;
}

const SavedSearches: React.FC<SavedSearchesProps> = ({ currentCriteria, onLoadSearch }) => {
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchName, setSearchName] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadSavedSearches();
    }
  }, [user]);

  const loadSavedSearches = async () => {
    if (!user) return;

    // For now, use localStorage until we implement saved searches in the database
    const saved = localStorage.getItem(`saved_searches_${user.id}`);
    if (saved) {
      setSavedSearches(JSON.parse(saved));
    }
  };

  const saveSearch = async () => {
    if (!user || !searchName.trim()) return;

    const newSearch: SavedSearch = {
      id: Date.now().toString(),
      name: searchName.trim(),
      criteria: currentCriteria,
      created_at: new Date().toISOString(),
    };

    const updatedSearches = [...savedSearches, newSearch];
    setSavedSearches(updatedSearches);
    localStorage.setItem(`saved_searches_${user.id}`, JSON.stringify(updatedSearches));
    
    setSearchName('');
    setIsOpen(false);
  };

  const deleteSearch = async (id: string) => {
    if (!user) return;

    const updatedSearches = savedSearches.filter(search => search.id !== id);
    setSavedSearches(updatedSearches);
    localStorage.setItem(`saved_searches_${user.id}`, JSON.stringify(updatedSearches));
  };

  const getCriteriaDescription = (criteria: any) => {
    const parts: string[] = [];
    
    if (criteria.searchTerm) parts.push(`"${criteria.searchTerm}"`);
    if (criteria.selectedCounties.length > 0) parts.push(`${criteria.selectedCounties.length} counties`);
    if (criteria.selectedWaivers.length > 0) parts.push(`${criteria.selectedWaivers.length} waivers`);
    if (criteria.selectedCareLevel) parts.push(criteria.selectedCareLevel);
    if (criteria.priceRange[0] > 0 || criteria.priceRange[1] < 1000) {
      parts.push(`$${criteria.priceRange[0]}-$${criteria.priceRange[1]}`);
    }
    if (criteria.bedsAvailable > 1) parts.push(`${criteria.bedsAvailable}+ beds`);
    if (criteria.ageGroup) parts.push(criteria.ageGroup);
    if (criteria.genderAccepted) parts.push(criteria.genderAccepted);
    
    return parts.length > 0 ? parts.join(', ') : 'No filters';
  };

  if (!user) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5" />
            Saved Searches
          </CardTitle>
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Save className="h-4 w-4 mr-2" />
                Save Current Search
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Save Search</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="search-name">Search Name</Label>
                  <Input
                    id="search-name"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    placeholder="Enter a name for this search"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Current Criteria:</Label>
                  <p className="text-sm text-muted-foreground">
                    {getCriteriaDescription(currentCriteria)}
                  </p>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setIsOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={saveSearch} disabled={!searchName.trim()}>
                    Save Search
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {savedSearches.length === 0 ? (
          <p className="text-sm text-muted-foreground">No saved searches yet.</p>
        ) : (
          <div className="space-y-2">
            {savedSearches.map(search => (
              <div key={search.id} className="flex items-center justify-between p-2 border rounded-md">
                <div className="flex-1">
                  <div className="font-medium text-sm">{search.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {getCriteriaDescription(search.criteria)}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onLoadSearch(search.criteria)}
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteSearch(search.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SavedSearches;
